import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '../../../../context/auth';
import '../Forms/AIChatModal.css';

const QuestionAIChatPane = ({
    topicsCategories = [],
    textTypesList = [],
    dimensionComponentsList = [],
    elementsList = [],
    SkillIds = [],
    TopicIds = [],
    Level = '',
    QuestionId = 0,
    QuestionLessonId = null,
    skillDimensionsMap = {},
    initialChatHistory = [],
    initialAiOptions = {},
    onUpdateAiHtml = () => {},
    onSetAiStreaming = () => {},
    onInsertAiHtml = () => {},
    onChatHistoryChange = () => {},
    onAiOptionsChange = () => {}
}) => {
    const { t } = useTranslation('common');

    const [prompt, setPrompt] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingText, setStreamingText] = useState('');
    const [chatHistory, setChatHistory] = useState(Array.isArray(initialChatHistory) ? initialChatHistory : []);

    const [selectedModel, setSelectedModel] = useState(initialAiOptions.selectedModel || 'deepseek');
    const [paragraphCount, setParagraphCount] = useState(initialAiOptions.paragraphCount || 3);
    const [wordCount, setWordCount] = useState(initialAiOptions.wordCount || 200);

    const getInitialTextLevel = () => {
        if (initialAiOptions.textLevel) return initialAiOptions.textLevel;
        if (Level === 1 || Level === '1') return 'easy';
        if (Level === 2 || Level === '2') return 'mid';
        if (Level === 3 || Level === '3') return 'high';
        return 'mid';
    };

    const [textLevel, setTextLevel] = useState(getInitialTextLevel());

    const [selectedSkillIds, setSelectedSkillIds] = useState(
        Array.isArray(initialAiOptions.selectedSkillIds)
            ? initialAiOptions.selectedSkillIds
            : (Array.isArray(TopicIds) ? [...TopicIds] : [])
    );
    const [globalTextTypeIds, setGlobalTextTypeIds] = useState(initialAiOptions.globalTextTypeIds || []);
    const [globalDimensionComponentIds, setGlobalDimensionComponentIds] = useState(initialAiOptions.globalDimensionComponentIds || []);
    const [globalElementIds, setGlobalElementIds] = useState(initialAiOptions.globalElementIds || []);

    const onChatHistoryChangeRef = useRef(onChatHistoryChange);
    useEffect(() => {
        onChatHistoryChangeRef.current = onChatHistoryChange;
    }, [onChatHistoryChange]);

    const onAiOptionsChangeRef = useRef(onAiOptionsChange);
    useEffect(() => {
        onAiOptionsChangeRef.current = onAiOptionsChange;
    }, [onAiOptionsChange]);

    useEffect(() => {
        if (Array.isArray(initialChatHistory) && initialChatHistory.length > 0 && chatHistory.length === 0) {
            setChatHistory(initialChatHistory);
        }
    }, [initialChatHistory, chatHistory.length]);

    useEffect(() => {
        if (onChatHistoryChangeRef.current) {
            onChatHistoryChangeRef.current(chatHistory);
        }
    }, [chatHistory]);

    useEffect(() => {
        if (onAiOptionsChangeRef.current) {
            onAiOptionsChangeRef.current({
                selectedModel,
                paragraphCount,
                wordCount,
                textLevel,
                selectedSkillIds,
                globalTextTypeIds,
                globalDimensionComponentIds,
                globalElementIds
            });
        }
    }, [selectedModel, paragraphCount, wordCount, textLevel, selectedSkillIds, globalTextTypeIds, globalDimensionComponentIds, globalElementIds]);

    const [showSkillTree, setShowSkillTree] = useState(false);
    const [skillSearchTerm, setSkillSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [openLists, setOpenLists] = useState({});
    const [searchLists, setSearchLists] = useState({});

    const treeSelectRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (treeSelectRef.current && !treeSelectRef.current.contains(event.target)) {
                setShowSkillTree(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (initialAiOptions && Object.keys(initialAiOptions).length > 0) return;
        let defaultSkillIds = Array.isArray(SkillIds) && SkillIds.length > 0 ? [...SkillIds] : (Array.isArray(TopicIds) ? [...TopicIds] : []);
        let defaultTT = [];
        let defaultDC = [];
        let defaultEL = [];

        if (skillDimensionsMap && typeof skillDimensionsMap === 'object') {
            Object.keys(skillDimensionsMap).forEach(topicId => {
                const dims = skillDimensionsMap[topicId] || {};
                if (dims.TextTypeId && !defaultTT.includes(dims.TextTypeId)) defaultTT.push(dims.TextTypeId);
                if (dims.DimensionComponentId && !defaultDC.includes(dims.DimensionComponentId)) defaultDC.push(dims.DimensionComponentId);
                if (dims.ElementId && !defaultEL.includes(dims.ElementId)) defaultEL.push(dims.ElementId);
            });
        }

        setSelectedSkillIds(defaultSkillIds);
        setGlobalTextTypeIds(defaultTT);
        setGlobalDimensionComponentIds(defaultDC);
        setGlobalElementIds(defaultEL);
    }, [SkillIds, TopicIds, skillDimensionsMap, initialAiOptions]);

    const handleSkillToggle = (topicId) => {
        setSelectedSkillIds(prev =>
            prev.includes(topicId) ? prev.filter(id => id !== topicId) : [...prev, topicId]
        );
    };

    const handleGlobalDimensionToggle = (setter, value) => {
        setter(prev => {
            const valStr = String(value);
            const exists = prev.map(String).includes(valStr);
            return exists ? prev.filter(x => String(x) !== valStr) : [...prev, value];
        });
    };

    const getSelectedSkillDetailsFormatted = () => {
        const rawCategories = Array.isArray(topicsCategories)
            ? topicsCategories
            : (Array.isArray(topicsCategories?.data) ? topicsCategories.data : []);

        const details = [];

        if (selectedSkillIds.length > 0) {
            const skillNames = [];
            rawCategories.forEach(cat => {
                if (cat && Array.isArray(cat.Topics)) {
                    cat.Topics.forEach(tNode => {
                        if (selectedSkillIds.includes(tNode.TopicId)) {
                            skillNames.push(tNode.TopicName);
                        }
                    });
                }
            });
            if (skillNames.length > 0) {
                details.push(`• ${t('AILessonChat.selectedSkills') || 'المهارات المختارة'}: ${skillNames.join('، ')}`);
            }
        }

        const ttNames = (textTypesList || []).filter(x => globalTextTypeIds.map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);
        const dcNames = (dimensionComponentsList || []).filter(x => globalDimensionComponentIds.map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);
        const elNames = (elementsList || []).filter(x => globalElementIds.map(String).includes(String(x.CategoryId))).map(x => x.CategoryName);

        if (ttNames.length > 0) details.push(`• ${t('AILessonChat.textTypes') || 'أنواع النصوص'}: ${ttNames.join('، ')}`);
        if (dcNames.length > 0) details.push(`• ${t('AILessonChat.textFamilies') || 'عائلات النصوص / المجالات'}: ${dcNames.join('، ')}`);
        if (elNames.length > 0) details.push(`• ${t('AILessonChat.elements') || 'العناصر'}: ${elNames.join('، ')}`);

        return details.join('\n');
    };

    const cleanMarkdownHtml = (rawText) => {
        if (!rawText) return '';
        let cleaned = rawText;
        cleaned = cleaned.replace(/```html/gi, '').replace(/```/g, '');
        return cleaned.trim();
    };

    const handleSend = async () => {
        if (!prompt || !prompt.trim() || isStreaming) return;

        const token = getToken();
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        const chatEndpoint = `${baseUrl}/ailessonchat`;

        const userMessage = prompt.trim();
        const isSubChat = chatHistory.length > 0;
        const skillDetailsFormatted = getSelectedSkillDetailsFormatted();

        setIsStreaming(true);
        if (onSetAiStreaming) onSetAiStreaming(true);
        if (onUpdateAiHtml) onUpdateAiHtml('');

        setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
        setStreamingText('');

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch(chatEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    Question: userMessage,
                    SkillName: skillDetailsFormatted,
                    ParagraphCount: parseInt(paragraphCount) || 3,
                    WordCount: parseInt(wordCount) || 200,
                    TextLevel: textLevel,
                    Grade: textLevel,
                    QuestionId: QuestionId || 0,
                    QuestionLessonId: QuestionLessonId || null,
                    PassCash: true,
                    Model: selectedModel || 'deepseek',
                    History: chatHistory.map(h => ({ Question: h.content, Answer: h.content })),
                    IsSubChat: isSubChat
                }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`خطأ في الاتصال بالخادم (${response.status})`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let accumulatedText = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    let jsonStr = trimmed;
                    if (trimmed.startsWith('data:')) {
                        jsonStr = trimmed.slice(5).trim();
                    }

                    if (jsonStr === '[DONE]') continue;

                    try {
                        const parsed = JSON.parse(jsonStr);
                        if (parsed.Answer !== undefined) {
                            accumulatedText += parsed.Answer;
                            setStreamingText(accumulatedText);
                            const cleanHtml = cleanMarkdownHtml(accumulatedText);
                            if (onUpdateAiHtml) onUpdateAiHtml(cleanHtml);
                        }
                    } catch (e) {
                        // Skip stream fragments
                    }
                }
            }

            setIsStreaming(false);
            setStreamingText('');
            if (onSetAiStreaming) onSetAiStreaming(false);
            setChatHistory(prev => [...prev, { role: 'assistant', content: accumulatedText }]);

        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error streaming AI lesson response:', err);
            setIsStreaming(false);
            setStreamingText('');
            if (onSetAiStreaming) onSetAiStreaming(false);
        }
    };

    const renderModernMultiSelectList = (title, icon, list, selectedIds, onToggle, instanceKey) => {
        const isOpen = openLists[instanceKey] || false;
        const searchTerm = searchLists[instanceKey] || '';

        const getItemId = (item) => item ? (item.CategoryId !== undefined ? item.CategoryId : (item.Id !== undefined ? item.Id : item.value)) : '';
        const getItemName = (item) => item ? (item.CategoryName || item.Name || item.text || item.title || item.Title || '') : '';

        const filtered = (list || []).filter(item =>
            getItemName(item).toLowerCase().includes(searchTerm.toLowerCase())
        );

        const selectedNames = (list || [])
            .filter(x => selectedIds.map(String).includes(String(getItemId(x))))
            .map(x => getItemName(x));

        return (
            <div className="modern-multi-select-container mb-2 w-100">
                <label className="small text-muted mb-1 d-block font-weight-bold">
                    <i className={`${icon} ml-1 text-primary`}></i>
                    {title}:
                    {selectedIds.length > 0 && (
                        <span className="badge badge-primary ml-1 font-weight-bold" style={{ fontSize: '0.75rem', borderRadius: '10px' }}>
                            {selectedIds.length} {t('AILessonChat.selected') || 'مختار'}
                        </span>
                    )}
                </label>
                <div className="modern-tree-select-wrapper w-100">
                    <div
                        className="modern-tree-select-trigger"
                        onClick={() => setOpenLists(prev => ({ ...prev, [instanceKey]: !isOpen }))}
                        style={{ minHeight: '40px', padding: '8px 12px' }}
                    >
                        <div className="modern-tree-select-values" style={{ fontSize: '0.85rem' }}>
                            {selectedNames.length > 0 ? selectedNames.join('، ') : `${t('AILessonChat.selectPrompt') || 'اختر'} ${title}...`}
                        </div>
                        <i className={`fad fa-chevron-down arrow-icon ${isOpen ? 'open' : ''}`}></i>
                    </div>

                    {isOpen && (
                        <div className="modern-tree-select-dropdown p-2" style={{ zIndex: 1060 }}>
                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                <div className="modern-tree-search-wrapper mb-0 flex-grow-1 ml-2" style={{ marginBottom: 0 }}>
                                    <input
                                        type="text"
                                        className="form-control modern-search-input"
                                        placeholder={`${t('AILessonChat.searchIn') || 'بحث في'} ${title}...`}
                                        value={searchTerm}
                                        onChange={e => setSearchLists(prev => ({ ...prev, [instanceKey]: e.target.value }))}
                                        onClick={e => e.stopPropagation()}
                                        style={{ height: '34px', fontSize: '0.82rem' }}
                                    />
                                    <i className="fal fa-search search-icon" style={{ fontSize: '0.85rem' }}></i>
                                </div>
                            </div>

                            <div className="modern-select-list-items" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {filtered.length > 0 ? (
                                    filtered.map(item => {
                                        const itemId = getItemId(item);
                                        const itemName = getItemName(item);
                                        const isChecked = selectedIds.map(String).includes(String(itemId));
                                        return (
                                            <label
                                                key={itemId}
                                                className={`modern-tree-item py-1 px-2 rounded cursor-pointer d-flex align-items-center justify-content-between mb-1 ${isChecked ? 'bg-light font-weight-bold text-primary' : ''}`}
                                                onClick={e => e.stopPropagation()}
                                                style={{ fontSize: '0.84rem' }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => onToggle(itemId)}
                                                    />
                                                    <span className="checkbox-custom ml-1"></span>
                                                    <span className="topic-name ml-1">{itemName}</span>
                                                </div>
                                                {isChecked && <i className="far fa-check text-primary"></i>}
                                            </label>
                                        );
                                    })
                                ) : (
                                    <div className="text-muted text-center py-2 small">
                                        {t('AILessonChat.noMatchingResults') || 'لا توجد نتائج مطابقة'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const isSubChat = chatHistory.length > 0;
    const rawCategories = Array.isArray(topicsCategories)
        ? topicsCategories
        : (Array.isArray(topicsCategories?.data) ? topicsCategories.data : []);

    const filteredCategories = rawCategories.map(cat => {
        const matchedTopics = (cat.Topics || []).filter(topic =>
            topic.TopicName.toLowerCase().includes(skillSearchTerm.toLowerCase())
        );
        return { ...cat, Topics: matchedTopics };
    }).filter(cat => cat.Topics.length > 0);

    return (
        <div className="row h-100 m-0 ai-embedded-pane-body modern-info-container">
            {/* Side A: Form Controls & Options Pane (Right Side in RTL) */}
            <div className="col-md-6 p-2 d-flex flex-column h-100 overflow-auto border-left">
                <div className="ai-options-card mb-3">
                    <div className="ai-options-title">
                        <i className="fal fa-sliders-h ml-1"></i>
                        {t('AILessonChat.optionsTitle') || 'معايير خيارات الدرس والقوائم المنسدلة'}
                    </div>

                    <div className="row mb-2">
                        <div className="col-12 mb-2">
                            <label className="ai-field-label font-weight-bold d-block text-muted small">
                                <i className="fal fa-robot text-primary ml-1"></i>
                                {t('AILessonChat.modelLabel') || 'نموذج الذكاء الاصطناعي:'}
                            </label>
                            <select
                                className="form-control form-control-sm modern-input-field font-weight-bold text-primary"
                                style={{ height: '38px', borderRadius: '8px', fontSize: '0.85rem' }}
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                            >
                                <option value="deepseek">🤖 {t('AILessonChat.chatBotModel') || 'مساعد الذكاء الاصطناعي (DeepSeek ChatBot)'}</option>
                                <option value="gemini">✨ {t('AILessonChat.geminiModel') || 'جيميني (Gemini)'}</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mb-2">
                        <div className="col-6 mb-2">
                            <label className="ai-field-label">
                                <i className="fal fa-align-left text-primary ml-1"></i>
                                {t('AILessonChat.paragraphCount') || 'عدد الفقرات (3):'}
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="20"
                                className="ai-input-num w-100"
                                value={paragraphCount}
                                onChange={(e) => setParagraphCount(e.target.value)}
                            />
                        </div>
                        <div className="col-6 mb-2">
                            <label className="ai-field-label">
                                <i className="fal fa-font text-primary ml-1"></i>
                                {t('AILessonChat.wordCount') || 'عدد الكلمات (200):'}
                            </label>
                            <input
                                type="number"
                                min="50"
                                max="3000"
                                className="ai-input-num w-100"
                                value={wordCount}
                                onChange={(e) => setWordCount(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="row mb-2">
                        <div className="col-12 mb-2">
                            <label className="ai-field-label font-weight-bold d-block text-muted small">
                                <i className="fal fa-layer-group text-primary ml-1"></i>
                                {t('AILessonChat.textLevelLabel') || 'مستوى صعوبة النص:'}
                            </label>
                            <select
                                className="form-control form-control-sm modern-input-field font-weight-bold"
                                style={{ height: '36px', borderRadius: '8px', fontSize: '0.85rem' }}
                                value={textLevel}
                                onChange={(e) => setTextLevel(e.target.value)}
                            >
                                <option value="easy">🟢 {t('AILessonChat.levelEasy') || 'سهل'}</option>
                                <option value="mid">🟡 {t('AILessonChat.levelMid') || 'متوسط'}</option>
                                <option value="high">🔴 {t('AILessonChat.levelHigh') || 'صعب'}</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mb-2">
                        <div className="col-md-4 mb-2">
                            {renderModernMultiSelectList(
                                t('AILessonChat.textTypes') || 'أنواع النصوص',
                                'fal fa-file-alt',
                                textTypesList,
                                globalTextTypeIds,
                                (val) => handleGlobalDimensionToggle(setGlobalTextTypeIds, val),
                                'pane_tt'
                            )}
                        </div>
                        <div className="col-md-4 mb-2">
                            {renderModernMultiSelectList(
                                t('AILessonChat.textFamilies') || 'عائلات النصوص',
                                'fal fa-puzzle-piece',
                                dimensionComponentsList,
                                globalDimensionComponentIds,
                                (val) => handleGlobalDimensionToggle(setGlobalDimensionComponentIds, val),
                                'pane_dc'
                            )}
                        </div>
                        <div className="col-md-4 mb-2">
                            {renderModernMultiSelectList(
                                t('AILessonChat.elements') || 'العناصر',
                                'fal fa-cube',
                                elementsList,
                                globalElementIds,
                                (val) => handleGlobalDimensionToggle(setGlobalElementIds, val),
                                'pane_el'
                            )}
                        </div>
                    </div>

                    <div className="ai-field-group mb-2">
                        <label className="small text-muted d-block font-weight-bold">
                            {t('AILessonChat.skillTreeLabel') || 'اختيار المهارات (شجرة المهارات):'}
                            {selectedSkillIds.length > 0 && (
                                <span className="badge badge-primary ml-1 font-weight-bold" style={{ fontSize: '0.75rem', borderRadius: '10px' }}>
                                    {selectedSkillIds.length} {t('AILessonChat.selected') || 'مختار'}
                                </span>
                            )}
                        </label>
                        <div className="modern-tree-select-wrapper w-100" ref={treeSelectRef}>
                            <div
                                className="modern-tree-select-trigger"
                                onClick={() => setShowSkillTree(!showSkillTree)}
                            >
                                <div className="modern-tree-select-values">
                                    {selectedSkillIds.length > 0
                                        ? `${t('AILessonChat.selectedSkills') || 'المهارات المختارة'} (${selectedSkillIds.length})`
                                        : (t('AILessonChat.selectSkillsFromTree') || 'اختر المهارات من الشجرة...')}
                                </div>
                                <i className={`fad fa-chevron-down arrow-icon ${showSkillTree ? 'open' : ''}`}></i>
                            </div>
                            {showSkillTree && (
                                <div className="modern-tree-select-dropdown" style={{ zIndex: 1050 }}>
                                    <div className="modern-tree-search-wrapper">
                                        <input
                                            type="text"
                                            className="form-control modern-search-input"
                                            placeholder={t('AILessonChat.searchSkills') || 'بحث في المهارات...'}
                                            value={skillSearchTerm}
                                            onChange={e => setSkillSearchTerm(e.target.value)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        <i className="fal fa-search search-icon"></i>
                                    </div>
                                    <div className="modern-tree-nodes">
                                        {filteredCategories.map(cat => (
                                            <div key={cat.CategoryId} className="modern-tree-category">
                                                <div
                                                    className="modern-category-header"
                                                    onClick={() => setExpandedCategories(prev =>
                                                        prev.includes(cat.CategoryId) ? prev.filter(id => id !== cat.CategoryId) : [...prev, cat.CategoryId]
                                                    )}
                                                >
                                                    <i className={`far ${expandedCategories.includes(cat.CategoryId) || skillSearchTerm ? 'fa-folder-open' : 'fa-folder'} category-folder-icon`}></i>
                                                    <span className="category-name">{cat.CategoryName}</span>
                                                    <span className="category-badge">{cat.Topics.length}</span>
                                                </div>
                                                {(expandedCategories.includes(cat.CategoryId) || skillSearchTerm) && (
                                                    <div className="modern-category-children">
                                                        {cat.Topics.map(topic => {
                                                            const sid = topic.SkillId || topic.TopicId;
                                                            const sName = topic.SkillName || topic.TopicName;
                                                            return (
                                                                <label key={sid} className="modern-tree-item" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedSkillIds.includes(sid)}
                                                                        onChange={() => handleSkillToggle(sid)}
                                                                    />
                                                                    <span className="checkbox-custom"></span>
                                                                    <span className="topic-name">{sName}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Side B: ChatBot Interface (Left Side in RTL) */}
            <div className="col-md-6 p-2 d-flex flex-column h-100 bg-white rounded">
                <div className="chatbot-header pb-2 mb-2 border-bottom d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <div className="chatbot-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                            <i className="fal fa-robot" style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                            <h6 className="font-weight-bold mb-0 text-primary">{t('AILessonChat.chatBotTitle') || 'مساعد الذكاء الاصطناعي صانع الدروس'}</h6>
                            <span className="small text-muted d-flex align-items-center gap-1">
                                <span className="badge badge-success rounded-circle p-1 d-inline-block"></span>
                                {isStreaming ? (t('AILessonChat.processing') || 'جاري البث والتوليد...') : 'جاهز ومتصل'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ChatBot Scroll Body */}
                <div className="chatbot-messages-body flex-grow-1 p-3 bg-light rounded border overflow-auto mb-2" style={{ minHeight: '320px', maxHeight: '520px' }}>
                    {chatHistory && chatHistory.length > 0 ? (
                        chatHistory.map((msg, index) => (
                            <div key={index} className={`mb-3 d-flex flex-column ${msg.role === 'user' ? 'align-items-end' : 'align-items-start'}`}>
                                <div className="small text-muted mb-1 font-weight-bold">
                                    <i className={`fal ${msg.role === 'user' ? 'fa-user ml-1 text-info' : 'fa-sparkles ml-1 text-warning'}`}></i>
                                    {msg.role === 'user' ? 'سؤالك:' : 'إجابة وتنسيق AI:'}
                                </div>
                                <div className={`p-3 rounded shadow-sm w-100 ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border text-dark'}`}>
                                    <div className="ai-response-html" dangerouslySetInnerHTML={{ __html: cleanMarkdownHtml(msg.content) }} />
                                    {msg.role === 'assistant' && (
                                        <div className="d-flex gap-2 mt-2 pt-2 border-top justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary font-weight-bold"
                                                onClick={() => onInsertAiHtml && onInsertAiHtml(cleanMarkdownHtml(msg.content), false)}
                                            >
                                                <i className="fal fa-file-import ml-1"></i>
                                                {t('AILessonChat.insertReplace') || 'إدراج (استبدال)'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary font-weight-bold"
                                                onClick={() => onInsertAiHtml && onInsertAiHtml(cleanMarkdownHtml(msg.content), true)}
                                            >
                                                <i className="fal fa-plus-circle ml-1"></i>
                                                {t('AILessonChat.append') || 'إلحاق'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        !isStreaming && (
                            <div className="text-center text-muted my-5 py-5">
                                <div className="display-4 text-primary opacity-50 mb-3"><i className="fal fa-comments-alt"></i></div>
                                <h6 className="font-weight-bold text-secondary">{t('AILessonChat.chatHistoryTitle') || 'سجل المحادثة والبث المباشر'}</h6>
                                <p className="small">اكتب عنوان أو موضوع الدرس في مربع المحادثة بالأسفل واضغط إرسال لبدء التوليد والبث المباشر.</p>
                            </div>
                        )
                    )}

                    {isStreaming && (
                        <div className="mb-3 d-flex flex-column align-items-start w-100">
                            <div className="small text-primary mb-1 font-weight-bold d-flex align-items-center gap-1">
                                <i className="fad fa-sync-alt fa-spin text-primary"></i>
                                جاري البث المباشر (Live Stream)...
                            </div>
                            <div className="p-3 rounded bg-white border border-primary shadow-sm w-100">
                                <div className="ai-response-html" dangerouslySetInnerHTML={{ __html: cleanMarkdownHtml(streamingText) }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* ChatBot Input Box at the Bottom of Left Pane */}
                <div className="chatbot-input-footer mt-auto pt-2 border-top">
                    <div className="ai-chat-input-group d-flex gap-2">
                        <textarea
                            className="form-control ai-chat-textarea flex-grow-1"
                            style={{ borderRadius: '8px', minHeight: '50px', fontSize: '0.88rem' }}
                            placeholder={
                                isSubChat
                                    ? (t('AILessonChat.textareaPlaceholderSub') || 'اطلب التعديل أو الإصلاح المطلوب على كود الدرس...')
                                    : (t('AILessonChat.textareaPlaceholderInitial') || 'اكتب عنوان أو موضوع الدرس، أو ألصق النص المراد صياغته وتنسيقه بـ HTML...')
                            }
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            disabled={isStreaming}
                        />
                        <button
                            className="btn btn-primary ai-send-btn align-self-end px-3 py-2 font-weight-bold"
                            style={{ borderRadius: '8px', minHeight: '46px' }}
                            onClick={handleSend}
                            disabled={!prompt.trim() || isStreaming}
                        >
                            {isStreaming ? (
                                <div className="ai-spinner"></div>
                            ) : (
                                <>
                                    <i className="fal fa-paper-plane ml-1"></i>
                                    {isSubChat
                                        ? (t('AILessonChat.btnUpdate') || 'تعديل')
                                        : (t('AILessonChat.btnGenerate') || 'إرسال')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestionAIChatPane;
